import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { gmailSchema } from "@zeeta/shared";
import { ArrowLeft } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { ErrorMessage } from "@/components/error-message";
import { LanguageToggle } from "@/components/language-toggle";
import { Logo } from "@/components/logo";
import { SiteFooter } from "@/components/site-footer";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/field";
import { type MessageKey, useI18n } from "@/i18n";
import { api } from "@/lib/api";
import { PasswordReset } from "./password-reset";
import { configQuery, refreshMe } from "./queries";
import { Turnstile, type TurnstileHandle } from "./turnstile";

interface Challenge {
  challengeId: string;
  expiresAt: number;
  resendAfter: number;
}

type Mode = "password" | "code" | "forgot";

function useCountdown(until: number | null): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!until) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [until]);
  return until ? Math.max(0, Math.ceil((until - now) / 1000)) : 0;
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.7z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z"
      />
      <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a12 12 0 0 0 0 10.8l4-3.1z" />
      <path
        fill="#EA4335"
        d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z"
      />
    </svg>
  );
}

function Terms() {
  const { t, locale } = useI18n();
  return (
    <p className="text-center text-xs text-muted-foreground">
      {t("legal.agreePrefix")}{" "}
      <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
        {t("legal.terms")}
      </Link>{" "}
      {t("legal.and")}{" "}
      <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
        {t("legal.privacy")}
      </Link>
      {locale === "bn" ? ` ${t("legal.agreeSuffix")}` : t("legal.agreeSuffix")}
    </p>
  );
}

function TrustDevice({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  const { t } = useI18n();
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 size-4 accent-primary"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
      {t("auth.trustDevice")}
    </label>
  );
}

export function LoginPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { email?: string; error?: string };
  const config = useQuery(configQuery);
  const turnstile = useRef<TurnstileHandle>(null);

  // Email and password first; the emailed code is the other way in.
  const [mode, setMode] = useState<Mode>("password");
  const [emailInput, setEmailInput] = useState(search.email ?? "");
  const [password, setPassword] = useState("");
  const [inputError, setInputError] = useState<string>();
  const [token, setToken] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  const secondsLeft = useCountdown(challenge?.resendAfter ?? null);
  const passwordEnabled = config.data?.passwordEnabled !== false;
  // When the platform turns password sign-in off, the emailed code is the only way.
  useEffect(() => {
    if (!passwordEnabled) setMode((m) => (m === "password" ? "code" : m));
  }, [passwordEnabled]);

  const signedIn = async () => {
    await refreshMe(queryClient);
    await navigate({ to: "/" });
  };

  const sendCode = useMutation({
    mutationFn: (email: string) =>
      api.post<Challenge>("/auth/otp/request", { email, turnstileToken: token, locale }),
    onSuccess: (result, email) => {
      setChallenge(result);
      setSentTo(email);
      setCode("");
    },
    onSettled: () => turnstile.current?.reset(),
  });

  const verify = useMutation({
    mutationFn: () =>
      api.post<{ isNewUser: boolean }>("/auth/otp/verify", {
        challengeId: challenge?.challengeId,
        code,
        trustDevice,
      }),
    onSuccess: signedIn,
  });

  const login = useMutation({
    mutationFn: (email: string) =>
      api.post("/auth/password/login", { email, password, trustDevice, turnstileToken: token }),
    onSuccess: signedIn,
    onSettled: () => turnstile.current?.reset(),
  });

  function readEmail(): string | null {
    const parsed = gmailSchema.safeParse(emailInput);
    if (!parsed.success) {
      setInputError(t("auth.emailInvalid"));
      return null;
    }
    setInputError(undefined);
    return parsed.data;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const email = readEmail();
    if (!email) return;
    if (mode === "password") login.mutate(email);
    else sendCode.mutate(email);
  }

  function switchMode(next: Mode) {
    setMode(next);
    setInputError(undefined);
    sendCode.reset();
    login.reset();
  }

  const googleError = search.error?.startsWith("google_")
    ? t(`auth.errors.${search.error}` as MessageKey)
    : null;

  let body: ReactNode;
  if (challenge) {
    body = (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (/^\d{6}$/.test(code)) verify.mutate();
        }}
        className="space-y-5"
        noValidate
      >
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            setChallenge(null);
            verify.reset();
          }}
        >
          <ArrowLeft aria-hidden />
          {t("auth.changeEmail")}
        </Button>
        <p className="text-sm text-muted-foreground">
          {t("auth.codeSentToEmail", { email: sentTo })}
        </p>
        <Field label={t("auth.codeLabel")}>
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              pattern="\d{6}"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="h-12 text-center text-2xl tracking-[0.5em] tabular-nums"
            />
          )}
        </Field>
        <TrustDevice value={trustDevice} onChange={setTrustDevice} />
        <ErrorMessage error={verify.error ?? sendCode.error} />
        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={verify.isPending}
          disabled={code.length !== 6}
        >
          {t("auth.verify")}
        </Button>
        <div className="space-y-3 border-t pt-4 text-center text-sm">
          {secondsLeft > 0 ? (
            <p className="text-muted-foreground">{t("auth.resendIn", { seconds: secondsLeft })}</p>
          ) : (
            <>
              <Turnstile ref={turnstile} action="otp_request" onToken={setToken} />
              <Button
                variant="outline"
                className="w-full"
                disabled={!token}
                loading={sendCode.isPending}
                onClick={() => sendCode.mutate(sentTo)}
              >
                {t("auth.resend")}
              </Button>
            </>
          )}
        </div>
      </form>
    );
  } else if (mode === "forgot") {
    body = (
      <PasswordReset
        signedIn={false}
        initialEmail={emailInput}
        onDone={signedIn}
        onBack={() => switchMode("password")}
      />
    );
  } else {
    body = (
      <div className="space-y-5">
        <div className="space-y-1.5">
          <h1 className="text-xl font-semibold tracking-tight">{t("auth.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("auth.subtitle")}</p>
        </div>
        {googleError && (
          <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive" role="alert">
            {googleError}
          </p>
        )}
        {config.data?.googleEnabled && (
          <>
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={() => {
                window.location.assign(`/api/auth/google/start?trust=${trustDevice ? "1" : "0"}`);
              }}
            >
              <GoogleMark />
              {t("auth.google")}
            </Button>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {t("auth.or")}
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}
        {passwordEnabled && (
          <div role="tablist" className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
            {(["password", "code"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={mode === m}
                className="rounded-md py-1.5 text-sm font-medium text-muted-foreground aria-selected:bg-card aria-selected:text-foreground aria-selected:shadow-sm"
                onClick={() => switchMode(m)}
              >
                {m === "code" ? t("auth.tabCode") : t("auth.tabPassword")}
              </button>
            ))}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4" noValidate>
          <Field label={t("auth.emailLabel")} error={inputError}>
            {(p) => (
              <Input
                {...p}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoFocus
                placeholder={t("auth.emailPlaceholder")}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="h-12 text-base"
              />
            )}
          </Field>
          {mode === "password" && (
            <>
              <Field label={t("auth.passwordLabel")}>
                {(p) => (
                  <Input
                    {...p}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 text-base"
                  />
                )}
              </Field>
              <TrustDevice value={trustDevice} onChange={setTrustDevice} />
            </>
          )}
          <Turnstile
            ref={turnstile}
            action={mode === "password" ? "password_login" : "otp_request"}
            onToken={setToken}
          />
          <ErrorMessage error={mode === "password" ? login.error : sendCode.error} />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            loading={sendCode.isPending || login.isPending}
            disabled={!token || (mode === "password" && password.length === 0)}
          >
            {mode === "password" ? t("auth.signIn") : t("auth.sendCode")}
          </Button>
          {mode === "password" && (
            <Button
              variant="link"
              size="sm"
              className="w-full"
              onClick={() => switchMode("forgot")}
            >
              {t("auth.forgotLink")}
            </Button>
          )}
        </form>
        <Terms />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-muted/40">
      <header className="flex items-center justify-between px-4 py-3 sm:px-6">
        <Logo />
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <LanguageToggle />
        </div>
      </header>
      <main className="flex flex-1 items-start justify-center px-4 pt-6 pb-16 sm:items-center sm:pt-0">
        <Card className="w-full max-w-sm">
          <CardContent className="space-y-6">{body}</CardContent>
        </Card>
      </main>
      <SiteFooter />
    </div>
  );
}
